import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="workday-stats"]',
        title: 'Thống kê tổng quan',
        description:
            'Hiển thị các chỉ số chính:\n• Tổng nhân viên\n• Tổng ngày công\n• Số ngày đi muộn\n• Ngày công trung bình',
    },
    {
        selector: '[data-tour="workday-chart"]',
        title: 'Biểu đồ phân tích',
        description:
            'Biểu đồ drill-down theo phòng ban.\n• Click vào cột để xem chi tiết nhân viên\n• Hiển thị: Ngày công, Đi muộn, Nghỉ phép, Remote\n• Sử dụng breadcrumb để quay lại',
    },
    {
        selector: '[data-tour="workday-top-employees"]',
        title: 'Top nhân viên',
        description:
            'Danh sách nhân viên nổi bật:\n• Nhân viên có ngày công cao nhất\n• Nhân viên remote nhiều nhất\n• Nhân viên ít đi muộn',
    },
    {
        selector: '[data-tour="workday-table"]',
        title: 'Bảng chi tiết nhân viên',
        description:
            'Danh sách đầy đủ ngày công nhân viên.\n• Sắp xếp theo cột\n• Phân trang dữ liệu\n• Xem chi tiết từng nhân viên',
    },
]

export const useWorkdayDashboardTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },

        []
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible elements to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useWorkdayDashboardTour
